import { useContext, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { FieldLabel } from "@/components/ui/field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import CustomTableService from "services/CustomTable";
import CustomTableImportService from "services/CustomTableImport";

import { ImportTargetContext } from "../ImportTargetContext";
import { formatBytes } from "../helpers";
import { formatError } from "../../helpers";

const MAX_BYTES = 50 * 1024 * 1024;
const ALLOWED_MIME = new Set([
  "text/csv",
  "application/csv",
  "application/vnd.ms-excel",
]);

const validateFile = file => {
  const isCsvName = file.name?.toLowerCase().endsWith(".csv");
  if (!ALLOWED_MIME.has(file.type) && !isCsvName) {
    return "That file is not a CSV. Please choose a .csv file.";
  }
  if (file.size > MAX_BYTES) {
    return `File too large (${formatBytes(file.size)}). The 50 MB file size limit applies.`;
  }
  return null;
};

const UPLOAD_ERROR_FALLBACK = "Upload failed. Please try again.";

const friendlyServerError = err => {
  const status = err?.response?.status;
  const code = err?.response?.data?.error;
  const limit = err?.response?.data?.limit;
  if (typeof status === "number" && status >= 400 && status < 500) {
    if (code === "ROW_LIMIT_EXCEEDED") {
      return `That file has too many rows. The row limit is ${limit ?? 100000}.`;
    }
    if (code === "MALFORMED_CSV") {
      return "We couldn't parse that CSV — check for unbalanced quotes or stray characters.";
    }
    if (code === "EMPTY_CSV") {
      return "That CSV is empty.";
    }
    if (code === "MISSING_HEADER") {
      return "That CSV is missing a header row.";
    }
    if (code === "UNSUPPORTED_ENCODING") {
      return "Only UTF-8 encoded CSV files are supported.";
    }
  }
  return formatError(err, UPLOAD_ERROR_FALLBACK);
};

const UploadStep = ({ onNext, showTablePicker = false }) => {
  const {
    mode,
    targetSlug,
    setKind,
    setMode,
    setTargetSlug,
    setFile,
    setParseResult,
    setMapping,
    setDataJobId,
    setTargetDisplayName,
    setExistingSchema,
  } = useContext(ImportTargetContext);
  const [selectedSlug, setSelectedSlug] = useState("");
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  const { data: tablesData, isLoading: tablesLoading } = useQuery({
    queryKey: ["custom_tables", "import-picker"],
    queryFn: () => CustomTableService.list({ per_page: 100 }),
    enabled: showTablePicker,
  });

  const tables = tablesData?.data ?? tablesData ?? [];
  const tableLabel = new Map(tables.map(t => [t.slug, t.display_name]));
  const fileDisabled = showTablePicker && !selectedSlug;

  const handleTableChange = value => {
    setSelectedSlug(value);
    setMode("append");
    setTargetSlug(value || null);
  };

  const handleSelect = async event => {
    setError(null);
    const file = event.target.files?.[0];
    if (!file) return;

    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setKind("new-table");
    setFile(file);
    setIsUploading(true);
    setProgress(0);

    try {
      const result = await CustomTableImportService.upload(
        file,
        evt => {
          if (evt.total) {
            setProgress(Math.round((evt.loaded / evt.total) * 100));
          }
        },
        "new-table",
        mode === "append" ? targetSlug : null
      );
      setParseResult(result);
      // A fresh upload must re-infer columns in the Mapping step. Clear any
      // mapping committed for a previously uploaded file so Step3Mapping
      // doesn't restore the old file's rows over the new one — it skips
      // inference whenever a saved mapping exists.
      setMapping(null);
      setDataJobId(result.data_job_id);
      if (result.existing_schema) {
        setExistingSchema(result.existing_schema);
        if (result.existing_schema.display_name) {
          setTargetDisplayName(result.existing_schema.display_name);
        }
      } else if (result.suggested_table_name) {
        setTargetDisplayName(result.suggested_table_name);
      }
      onNext?.();
    } catch (err) {
      setError(friendlyServerError(err));
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="tw:flex tw:flex-col tw:gap-4 tw:p-6">
      <h2 className="tw:font-heading tw:text-foreground tw:m-0 tw:text-2xl">
        Upload
      </h2>
      <p className="tw:text-muted-foreground tw:m-0">
        Drop a comma-separated file below. We'll parse the headers and a sample
        of rows to suggest column types.
      </p>

      {showTablePicker && (
        <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-3 tw:p-4 tw:bg-card tw:border tw:border-border tw:rounded-md">
          <FieldLabel
            htmlFor="append-table-select"
            className="tw:font-semibold tw:text-foreground"
          >
            Which table are you importing into?
          </FieldLabel>
          {tablesLoading ? (
            <div>Loading…</div>
          ) : (
            <Select
              value={selectedSlug || null}
              onValueChange={handleTableChange}
            >
              <SelectTrigger
                id="append-table-select"
                aria-label="Custom table to append to"
                className="tw:min-w-64"
              >
                <SelectValue placeholder="Select a table…">
                  {value => tableLabel.get(value) ?? value}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {tables.map(t => (
                  <SelectItem key={t.slug} value={t.slug}>
                    {t.display_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      )}

      <div
        className="tw:flex tw:items-center tw:justify-center tw:p-12 tw:bg-card tw:border-2 tw:border-dashed tw:border-border tw:rounded-lg tw:text-center tw:data-[disabled=true]:opacity-55"
        data-disabled={fileDisabled || undefined}
      >
        <label
          htmlFor="csv-file-input"
          className="tw:flex tw:flex-col tw:items-center tw:gap-2 tw:cursor-pointer tw:data-[disabled=true]:cursor-not-allowed"
          data-disabled={fileDisabled || undefined}
        >
          <span className="tw:font-heading tw:font-semibold tw:text-primary">
            Choose a file to upload
          </span>
          <input
            id="csv-file-input"
            type="file"
            onChange={handleSelect}
            disabled={fileDisabled}
            className="tw:sr-only"
          />
        </label>
      </div>

      {isUploading && (
        <div
          className="tw:w-full tw:h-2 tw:bg-accent tw:rounded-sm tw:overflow-hidden"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={progress}
        >
          <div
            className="tw:h-full tw:bg-primary tw:transition-[width] tw:duration-200"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}

      {error && (
        <div className="tw:px-4 tw:py-3 tw:bg-warning/10 tw:border tw:border-warning/20 tw:rounded-md tw:text-warning tw:dark:bg-warning/20 tw:text-sm">
          {error}
        </div>
      )}
    </div>
  );
};

export default UploadStep;
